import type { ReactNode } from "react";

import { useMe } from "@/hooks/useMe";
import { NoAdminAccessPage } from "@/pages/auth/NoAdminAccessPage";

/**
 * Admin-console access gate (server-authoritative).
 *
 * web-admin is internal-staff-only. Entry is gated on the two admin-console
 * permissions from the role matrix (rule §D.1): `canManageUsers` and
 * `canManageMasterData`. A user with NEITHER has no business in the console and
 * is shown the chrome-less "no admin access" page instead of any admin route.
 * (Per the current matrix only `admin` holds either permission, so AND/OR are
 * equivalent today; we gate on "has at least one" so a future role granted just
 * one of the two still gets in.)
 *
 * SECURITY: the check reads `role` from a FRESH `GET /v1/users/me` response, NOT
 * from the redux-persist / sessionStorage user cache. That cached user is
 * attacker-controllable — anyone can edit sessionStorage and flip a permission
 * boolean — so it must never decide access. The backend is authoritative (§L):
 * `/me` returns the real role row, and every admin write path is independently
 * guarded server-side by `PermissionsGuard`. This gate only hides the UI; it is
 * not the security boundary by itself.
 *
 * Fail-closed: while `/me` is in flight we render a neutral "checking" state
 * (never the admin shell), and any error fetching `/me` is treated as "no
 * access" rather than letting the console through unverified.
 */
export const RequireAdminAccess = ({ children }: { children: ReactNode }) => {
  const { data, isLoading, isError } = useMe();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-gray-400">
        Checking access…
      </div>
    );
  }

  const role = data?.user.role;
  const hasAdminAccess = !!role && (role.canManageUsers || role.canManageMasterData);

  if (isError || !hasAdminAccess) {
    return <NoAdminAccessPage />;
  }

  return <>{children}</>;
};
